import React, { ReactChild } from 'react'
import { createPortal } from 'react-dom';
import cls from 'classnames';
import FontAwesomeIcon from '../fa_icon';
import styles from './dropdown.module.scss';
import { Busy } from '../busy';

export interface DropdownItem {
  content: ReactChild;
  checked?: boolean;
  weight?: 'danger' | 'normal';
  busy?: boolean;
  disabled?: boolean;
  icon?: string;
  tag?: string;
  testId?: string;
  onClick?: () => void;
  href?: string;
}

export interface DropdownProps {
  items?: DropdownItem[];
  open?: boolean;
  align?: string;
  onRequestHide: () => void;
  onItemClicked?: (item: DropdownItem, index: number) => void;

  onMouseEnter?: (event: React.MouseEvent) => void;
  onMouseLeave?: (event: React.MouseEvent) => void;

  /**
   * Rendered in case no items are rendered.
   */
  placeholder?: string;

  /**
   * If set, then a matched item will be scrolled to once open.
   */
  autoScrollTo?: string;

  hideOnOutsideClick?: boolean;
}

/**
 * Dropdown which will be dismissed if clicked outside of it. Expects a relatively positioned parent container.
 * @author Johan Svensson
 */
export default function Dropdown({
  items,
  onRequestHide,
  align = 'left',
  autoScrollTo,
  hideOnOutsideClick = true,
  onItemClicked,
  onMouseEnter,
  onMouseLeave,
  open,
  placeholder
}: DropdownProps) {
  const [visible, setVisible] = React.useState(false);
  const dropdownElement = React.useRef<HTMLElement>(null);
  const anchorElement = React.useRef<HTMLElement>(null);
  const autoScrollTargetElement = React.useRef<HTMLElement>(null);
  const autoPositionDropdownRef = React.useRef<any>(null);
  const root = document.getElementById('dropdown-root');

  items = items.filter(Boolean);

  function autoScroll(scrollable = dropdownElement.current, target = autoScrollTargetElement.current) {
    if (!scrollable || !target || didAutoScroll.current || !open) {
      return;
    }

    const parentTop = target.parentElement.getBoundingClientRect().top;
    const top = target.getBoundingClientRect().top + scrollable.scrollTop - parentTop;

    scrollable.scrollTo({ top });
    didAutoScroll.current = true;
  }

  function positionDropdown(anchor = anchorElement.current, dropdown = dropdownElement.current) {
    if (!anchor || !dropdown) {
      return;
    }

    const maxY = window.innerHeight - 8;

    const offsetBounds = document.body.getBoundingClientRect();
    const anchorBounds = anchor.getBoundingClientRect();
    const dropdownBounds = dropdown.getBoundingClientRect();
    let top = (anchorBounds.top - offsetBounds.top * 0);
    let bottom = top + dropdownBounds.height;

    if (bottom > maxY) {
      top -= bottom - maxY;
    }

    dropdown.style.width = Math.max(anchorBounds.width, dropdownBounds.width) + 'px';
    dropdown.style.top = top + 'px';

    if (align === 'right') {
      dropdown.style.left = 'unset';
      dropdown.style.right = (root.getBoundingClientRect().width - anchorBounds.right - offsetBounds.left - window.scrollX) + 'px';
    } else {
      dropdown.style.right = 'unset';
      dropdown.style.left = (anchorBounds.left - offsetBounds.left - window.scrollX) + 'px';
    }
  }

  function autoPositionDropdown() {
    autoPositionDropdownRef.current = setInterval(() => {
      if (!dropdownElement.current) {
        return;
      }

      dropdownElement.current.classList.add(styles.transition);

      positionDropdown();
    }, 1000);
  }

  React.useEffect(() => {
    if (dropdownElement.current) {
      //  Resize as children changes.
      dropdownElement.current.style.width = null;
      positionDropdown();
    }
  }, [items]);

  const didAutoScroll = React.useRef(false);

  React.useEffect(() => {
    if (!open) {
      didAutoScroll.current = false;
      clearInterval(autoPositionDropdownRef.current);
      if (dropdownElement.current) {
        dropdownElement.current.classList.remove(styles.transition);
      }
    } else {
      setVisible(true);
      autoScroll();
      autoPositionDropdown();
    }
  }, [open]);

  if (!visible) {
    return null;
  }

  return (
    <div ref={e => {
      anchorElement.current = e;
      positionDropdown(e);
    }}>
      {createPortal((
        <>
          {
            open && hideOnOutsideClick ? (
              <div
                className={styles.backdrop}
                onClick={e => {
                  e.stopPropagation();
                  onRequestHide()
                }}></div>
            ) : null
          }
          <div
            ref={e => {
              dropdownElement.current = e;
              autoScroll(e);
              positionDropdown(anchorElement.current, e);
            }}
            className={cls({
              [styles.dropdown]: true,
              [styles.open]: open
            })}
            data-align={align}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onAnimationEnd={() => {
              if (!open) {
                setVisible(false)
              }
            }}>

            {
              items.length ? (
                items.map((item, i) => (
                  <DropdownItemComponent
                    key={item.tag || item.content as any}
                    _ref={autoScrollTo == item.content ? e => {
                      autoScrollTargetElement.current = e;
                      autoScroll(dropdownElement.current, e);
                    } : null}
                    item={item}
                    index={i}
                    onClick={() => {
                      onItemClicked?.(item, i);
                      item.onClick?.();
                    }} />
                ))
              ) : placeholder ? (
                <p className={styles.placeholder}>{placeholder}</p>
              ) : null
            }
          </div>
        </>
      ), root)}
    </div>
  )
}

function DropdownItemComponent(props: {
  _ref?: (e: HTMLElement) => void,
  item: DropdownItem,
  index: number,
  onClick?: any,
}) {
  const { item, index } = props;

  const children = (
    <>
      <div style={{
        display: 'inline-flex',
        flexDirection: 'row',
        alignItems: 'center'
      }}>
        {item.busy ? (
          <Busy className={cls([styles.custom, styles.busy])} />
        ) : item.icon ? (
          <FontAwesomeIcon className={cls([styles.custom, styles.icon])} name={item.icon} />
        ) : null}
        {item.content || 'alla'}
      </div>
      {typeof item.checked === 'boolean' ? (
        <DropdownlItemCheckbox item={item} />
      ) : null}
    </>
  );

  let weight = item.weight || 'normal';

  const componentProps = {
    key: index.toString(),
    className: cls([styles.item, styles[weight]]),
    "data-value": item.content,
    onClick: e => {
      e.stopPropagation();
      props.onClick?.(item, index);
    },
    disabled: item.disabled,
    children,
    "data-id": item.testId,
    href: item.href,
    rel: 'noopener noreferrer',
    target: '_blank'
  };

  return (
    <a ref={props._ref} {...componentProps} />
  );
}

/**
 * Checkbox for a dropdown item.
 */
function DropdownlItemCheckbox(props: { item: DropdownItem }) {
  return (
    <input type="checkbox" checked={props.item.checked} />
  )
}